import React, { useState } from 'react';
import { Copy, Check, MapPin, Building2, ExternalLink, Info, ShieldCheck } from 'lucide-react';
import { JAPAN_WAREHOUSE_BASE } from '../lib/firebase';

interface JapaneseAddressCardProps {
  suiteNumber: string;
  userName: string;
}

export const JapaneseAddressCard: React.FC<JapaneseAddressCardProps> = ({
  suiteNumber,
  userName
}) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const fullRecipient = `${JAPAN_WAREHOUSE_BASE.recipientBase} ${suiteNumber}`;
  const clientExtraField = `${userName} ${suiteNumber}`;
  const fullAddressLine = `${JAPAN_WAREHOUSE_BASE.addressLine1} ${suiteNumber}`;

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(label);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const copyFullAddressBlock = () => {
    const fullText = `
Nome / Name: ${fullRecipient}
CEP / Postal Code: ${JAPAN_WAREHOUSE_BASE.postalCode}
Estado / Prefecture: ${JAPAN_WAREHOUSE_BASE.prefecture}
Cidade / City: ${JAPAN_WAREHOUSE_BASE.city}
Endereço / Address: ${fullAddressLine}
Campo Adicional / Extra Field: ${clientExtraField}
Telefone / Phone: ${JAPAN_WAREHOUSE_BASE.phone}
País / Country: ${JAPAN_WAREHOUSE_BASE.country}
`.trim();
    copyToClipboard(fullText, 'full');
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200/80 shadow-2xs overflow-hidden transition-all hover:shadow-md">
      {/* Bento Header Banner in #1D3557 */}
      <div className="bg-[#1D3557] text-white p-4 sm:p-6 relative overflow-hidden">
        <div className="absolute -right-6 -bottom-6 opacity-10 text-white pointer-events-none">
          <Building2 size={160} />
        </div>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 relative z-10">
          <div>
            <div className="flex items-center gap-2 text-slate-300 text-xs font-bold uppercase tracking-wider mb-1">
              <MapPin className="w-4 h-4 text-[#E63946]" />
              Seu endereço de entrega no Japão
            </div>
            <h2 className="text-xl sm:text-2xl font-bold leading-tight">
              Imafuku 766-9, Kawagoe-shi, Saitama <br className="hidden sm:inline" />
              Suíte: <span className="text-[#E63946] font-black">{suiteNumber}</span>
            </h2>
          </div>
          <button
            onClick={copyFullAddressBlock}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 active:bg-white/30 text-white text-xs sm:text-sm font-bold rounded-xl backdrop-blur-sm transition-all border border-white/20 shadow-xs"
          >
            {copiedField === 'full' ? (
              <>
                <Check className="w-4 h-4 text-emerald-300" />
                <span>Copiado Completo!</span>
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                <span>COPIAR ENDEREÇO</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Address Details Grid */}
      <div className="p-4 sm:p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
          
          {/* Recipient */}
          <div className="p-3.5 bg-slate-50/80 rounded-xl border border-slate-200/60 relative group">
            <div className="flex justify-between items-start">
              <div>
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">
                  Nome do Destinatário (Name / お名前)
                </span>
                <span className="font-bold text-[#1D3557] text-sm sm:text-base break-words">
                  {fullRecipient}
                </span>
              </div>
              <button
                onClick={() => copyToClipboard(fullRecipient, 'recipient')}
                className="p-1.5 text-slate-400 hover:text-[#E63946] hover:bg-slate-200/50 rounded-lg transition-colors"
                title="Copiar Nome"
              >
                {copiedField === 'recipient' ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Postal Code */}
          <div className="p-3.5 bg-slate-50/80 rounded-xl border border-slate-200/60 relative group">
            <div className="flex justify-between items-start">
              <div>
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">
                  CEP / Postal Code (〒 郵便番号)
                </span>
                <span className="font-bold text-[#1D3557] text-sm sm:text-base font-mono">
                  {JAPAN_WAREHOUSE_BASE.postalCode}
                </span>
              </div>
              <button
                onClick={() => copyToClipboard(JAPAN_WAREHOUSE_BASE.postalCode, 'postal')}
                className="p-1.5 text-slate-400 hover:text-[#E63946] hover:bg-slate-200/50 rounded-lg transition-colors"
                title="Copiar CEP"
              >
                {copiedField === 'postal' ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Prefecture */}
          <div className="p-3.5 bg-slate-50/80 rounded-xl border border-slate-200/60 relative group">
            <div className="flex justify-between items-start">
              <div>
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">
                  Província / Prefecture (都道府県)
                </span>
                <span className="font-bold text-[#1D3557] text-sm sm:text-base">
                  {JAPAN_WAREHOUSE_BASE.prefecture}
                </span>
              </div>
              <button
                onClick={() => copyToClipboard(JAPAN_WAREHOUSE_BASE.prefecture, 'prefecture')}
                className="p-1.5 text-slate-400 hover:text-[#E63946] hover:bg-slate-200/50 rounded-lg transition-colors"
                title="Copiar Província"
              >
                {copiedField === 'prefecture' ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* City */}
          <div className="p-3.5 bg-slate-50/80 rounded-xl border border-slate-200/60 relative group">
            <div className="flex justify-between items-start">
              <div>
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">
                  Cidade e Bairro / City (市区町村)
                </span>
                <span className="font-bold text-[#1D3557] text-sm sm:text-base">
                  {JAPAN_WAREHOUSE_BASE.city}
                </span>
              </div>
              <button
                onClick={() => copyToClipboard(JAPAN_WAREHOUSE_BASE.city, 'city')}
                className="p-1.5 text-slate-400 hover:text-[#E63946] hover:bg-slate-200/50 rounded-lg transition-colors"
                title="Copiar Cidade"
              >
                {copiedField === 'city' ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Street Address + Suite */}
          <div className="p-3.5 bg-amber-50/70 border border-amber-200/80 rounded-xl md:col-span-2 relative group">
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-bold text-amber-800 uppercase tracking-wider">
                    Endereço Linha 1 + Suíte (町名・番地・建物名)
                  </span>
                  <span className="px-2 py-0.5 bg-[#E63946] text-white font-black text-[10px] rounded-md">
                    SUÍTE OBRIGATÓRIA
                  </span>
                </div>
                <span className="font-bold text-amber-950 text-base sm:text-lg block mt-1 font-mono">
                  {fullAddressLine}
                </span>
              </div>
              <button
                onClick={() => copyToClipboard(fullAddressLine, 'addressLine')}
                className="p-1.5 text-amber-700 hover:text-amber-950 hover:bg-amber-100 rounded-lg transition-colors"
                title="Copiar Endereço"
              >
                {copiedField === 'addressLine' ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Extra Field / Extra Name + Suite Option */}
          <div className="p-3.5 bg-sky-50/80 border border-sky-200/80 rounded-xl md:col-span-2 relative group">
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-bold text-sky-900 uppercase tracking-wider">
                    Campo Adicional / Observação no Site (Opcional)
                  </span>
                  <span className="px-2 py-0.5 bg-sky-600 text-white font-bold text-[10px] rounded-md">
                    NOME DO CLIENTE + SUÍTE
                  </span>
                </div>
                <span className="font-bold text-sky-950 text-base sm:text-lg block mt-1 font-mono">
                  {clientExtraField}
                </span>
                <p className="text-[11px] text-sky-700 mt-1">
                  Se o site de compra tiver um campo extra (edifício, complemento ou observação de entrega), você pode preencher com esta informação.
                </p>
              </div>
              <button
                onClick={() => copyToClipboard(clientExtraField, 'extraField')}
                className="p-1.5 text-sky-700 hover:text-sky-950 hover:bg-sky-100 rounded-lg transition-colors"
                title="Copiar Campo Adicional"
              >
                {copiedField === 'extraField' ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Phone */}
          <div className="p-3.5 bg-slate-50/80 rounded-xl border border-slate-200/60 relative group md:col-span-2">
            <div className="flex justify-between items-start">
              <div>
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">
                  Telefone do Depósito (Telephone / 電話番号)
                </span>
                <span className="font-bold text-[#1D3557] text-sm sm:text-base font-mono">
                  {JAPAN_WAREHOUSE_BASE.phone}
                </span>
              </div>
              <button
                onClick={() => copyToClipboard(JAPAN_WAREHOUSE_BASE.phone, 'phone')}
                className="p-1.5 text-slate-400 hover:text-[#E63946] hover:bg-slate-200/50 rounded-lg transition-colors"
                title="Copiar Telefone"
              >
                {copiedField === 'phone' ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>

        </div>

        {/* Informative Tip */}
        <div className="flex items-start gap-3 p-3.5 bg-blue-50/80 rounded-xl border border-blue-100 text-[#1D3557] text-xs sm:text-sm">
          <ShieldCheck className="w-5 h-5 text-[#1D3557] flex-shrink-0 mt-0.5" />
          <div>
            <span className="font-bold block text-[#1D3557]">Dica de Compra no Japão:</span>
            O nome do destinatário deve ser <strong className="text-slate-900 font-mono">Do Japao {suiteNumber}</strong>. Se o site tiver um campo a mais de preenchimento, pode adicionar seu nome e número da suíte (<strong className="text-slate-900 font-mono">{clientExtraField}</strong>).
          </div>
        </div>
      </div>
    </div>
  );
};
